import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect } from 'react';

/**
 * Right-hand detail drawer (06-designsystem.md). On mobile it becomes a
 * full-height sheet so approve/reject flows are completable on a phone.
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : 'Detaljer'}
            className="border-hairline bg-panel/95 backdrop-blur-glass fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l sm:max-w-xl lg:max-w-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <header className="border-hairline flex items-start gap-3 border-b px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="font-display text-ink text-base leading-snug">{title}</div>
                {subtitle && <div className="mt-1.5">{subtitle}</div>}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Lukk"
                className="text-ink-soft hover:bg-raised hover:text-ink -mr-1 -mt-1 rounded-lg p-2 transition"
              >
                <X size={18} aria-hidden />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

            {footer && (
              <footer className="border-hairline bg-base/40 border-t px-5 py-3">{footer}</footer>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
